import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  CircularProgress,
  Alert,
  Stack,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { formatMoney } from "../utils/formatMoney";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import API from "../api";
import { formatDate } from "../utils/formatDate";

function TillVarianceReportPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Report filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL");
  const [accounts, setAccounts] = useState([]);
  const [filterTillUser, setFilterTillUser] = useState("ALL");
  const [filterShiftManager, setFilterShiftManager] = useState("ALL");
  const [filterTillVarianceClass, setFilterTillVarianceClass] = useState("ALL");

  // Report data fetched from backend
  const [reportData, setReportData] = useState({ rows: [], totals: {}, pagination: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination state (1-based indexing)
  const [page, setPage] = useState(1);

  // Default date range: current month
  const defaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0],
    };
  };

  useEffect(() => {
    const { start, end } = defaultDateRange();
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      const res = await API.get("/accounts", { params: { limit: 1000000 } });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
      setError(err.response?.data?.message || "Error fetching accounts");
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Fetch report data (with optional append)
  const generateReport = async (pageNumber = 1, append = false) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        startDate,
        endDate,
        account: accountFilter,
        tillUser: filterTillUser,
        shiftManager: filterShiftManager,
        varianceClass: filterTillVarianceClass,
        page: pageNumber,
        limit: 50,
      };
      const res = await API.get("/advance-reports/till-variance", { params });
      setReportData(prev =>
        append
          ? {
              rows: [...prev.rows, ...res.data.rows],
              totals: res.data.totals,
              pagination: res.data.pagination,
            }
          : res.data
      );
      setPage(res.data.pagination.currentPage);
    } catch (err) {
      console.error("Error generating report:", err);
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  // Generate fresh report
  const handleGenerateReport = () => {
    generateReport(1);
  };

  // Load more pages
  const handleLoadMore = () => {
    if (page < reportData.pagination.totalPages) {
      generateReport(page + 1, true);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const queryParams = `?startDate=${startDate}&endDate=${endDate}&account=${accountFilter}&tillUser=${filterTillUser}&shiftManager=${filterShiftManager}&varianceClass=${filterTillVarianceClass}`;
      const response = await API.get(`/advance-reports/till-variance/export${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const csvBlob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "till-variance-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setError(error.response?.data?.message || "Error exporting CSV");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
        Till Variance Report
      </Typography>

      {/* Filter Panel */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={accounts}
            getOptionLabel={option => `${option.accountName} (${option.accountNo})`}
            value={
              accountFilter === "ALL"
                ? { _id: "ALL", accountName: "All Accounts" }
                : accounts.find(a => a._id === accountFilter) || null
            }
            onChange={(e, newValue) => setAccountFilter(newValue ? newValue._id : "ALL")}
            renderInput={params => <TextField {...params} label="Account" />}
            ListboxProps={{ style: { maxHeight: 240 } }}
            sx={{ width: 250 }}
          />
          <Button variant="contained" onClick={handleGenerateReport}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={handleExportCSV} sx={{ mb: 2 }}>
            Export to CSV
          </Button>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption">
            Date Range: {startDate} to {endDate}
          </Typography>
        </Box>

        {/* Additional Filters */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <TextField
            label="Filter by Till User"
            placeholder="Enter user name"
            value={filterTillUser}
            onChange={e => setFilterTillUser(e.target.value)}
          />
          <TextField
            label="Filter by Shift Manager"
            placeholder="Enter manager name"
            value={filterShiftManager}
            onChange={e => setFilterShiftManager(e.target.value)}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="till-variance-filter-label">Till Variance</InputLabel>
            <Select
              labelId="till-variance-filter-label"
              value={filterTillVarianceClass}
              label="Till Variance"
              onChange={e => setFilterTillVarianceClass(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="Surplus">Surplus</MenuItem>
              <MenuItem value="Deficit">Deficit</MenuItem>
              <MenuItem value="Zero">Zero</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          <Paper sx={{ overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Till Category</TableCell>
                  <TableCell>Declared Cash</TableCell>
                  <TableCell>Target Cash</TableCell>
                  <TableCell>Till Variance</TableCell>
                  <TableCell>Till Users</TableCell>
                  <TableCell>Shift Manager</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.rows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.accountName} {row.accountNo && `(${row.accountNo})`}</TableCell>
                    <TableCell>{row.tillCategory}</TableCell>
                    <TableCell>{formatMoney(row.declaredCash, reportData.currency)}</TableCell>
                    <TableCell>{formatMoney(row.targetCash, reportData.currency)}</TableCell>
                    <TableCell sx={{
                      color: Number(row.tillVariance) > 0 ? "green" :
                             Number(row.tillVariance) < 0 ? "red" : "inherit"
                    }}>
                      {formatMoney(row.tillVariance, reportData.currency)}
                    </TableCell>
                    <TableCell>{row.tillUsers}</TableCell>
                    <TableCell>{row.shiftManager}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    Totals:
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    {reportData.totals.totalDeclared}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    {reportData.totals.totalTarget}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                    {reportData.totals.totalVariance}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>

          {/* Load More Button */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            {page < reportData.pagination.totalPages && (
              <Button
                variant="contained"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? "Loading…" : "Load More"}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

export default TillVarianceReportPage;
